import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Training from './pages/Training';
import Payslips from './pages/Payslips';
import KPI from './pages/KPI';
import Assessment from './pages/Assessment';
import Leave from './pages/Leave';
import Overtime from './pages/Overtime';
import Monitoring from './pages/Monitoring';
import Employees from './pages/Employees';
import Audit from './pages/Audit';
import Settings from './pages/Settings';
import Locations from './pages/Locations';
import Governance from './pages/Governance';
import WorldMap from './pages/WorldMap';
import Timesheets from './pages/Timesheets';
import HRFinance from './pages/HRFinance';
import Recruitment from './pages/Recruitment';
import Contracts from './pages/Contracts';
import PerformanceReviews from './pages/PerformanceReviews';
import Disciplinary from './pages/Disciplinary';
import CompanyNews from './pages/CompanyNews';
import UploadEmployees from './pages/UploadEmployees';
import LoginRecordings from './pages/LoginRecordings';

// Layout
import Layout from './components/layout/Layout';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="training" element={<Training />} />
        <Route path="payslips" element={<Payslips />} />
        <Route path="kpi" element={<KPI />} />
        <Route path="assessment" element={<Assessment />} />
        <Route path="leave" element={<Leave />} />
        <Route path="overtime" element={<Overtime />} />
        <Route path="monitoring" element={<Monitoring />} />
        <Route path="employees" element={<Employees />} />
        <Route path="locations" element={<Locations />} />
        <Route path="audit" element={<Audit />} />
        <Route path="settings" element={<Settings />} />
        <Route path="payroll" element={<Payslips />} />
        <Route path="company" element={<Dashboard />} />
        <Route path="ai-insights" element={<KPI />} />
        <Route path="governance" element={<Governance />} />
        <Route path="world-map" element={<WorldMap />} />
        <Route path="timesheets" element={<Timesheets />} />
        <Route path="hr-finance" element={<HRFinance />} />
        <Route path="recruitment" element={<Recruitment />} />
        <Route path="contracts" element={<Contracts />} />
        <Route path="performance-reviews" element={<PerformanceReviews />} />
        <Route path="disciplinary" element={<Disciplinary />} />
        <Route path="company-news" element={<CompanyNews />} />
        <Route path="upload-employees" element={<UploadEmployees />} />
        <Route path="login-recordings" element={<LoginRecordings />} />
      </Route>

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
